import cx from "classnames";
import ReactFbq from "react-fbq";
import {
  IonContent,
  IonLabel,
  IonModal,
  IonPage,
  IonToast,
  useIonViewDidEnter,
} from "@ionic/react";

import s from "./WebSignUp.module.scss";
import useWebSignupStore from "./store";
import { Suspense, lazy, useEffect, useRef } from "react";
import { auth } from "../../firebase";
import { isEmpty, isNull } from "lodash";
import StripeForm from "./StripePaymentForm/StripeForm";
import {
  useGetReferralEntries,
  useSubscriptionsProvider,
  useWebSignupBootstrapProvider,
  useWebSignupPromoCodeProvider,
  useWebSignupProvider,
} from "./provider";
import brandLogo from "../../assets/images/wl-logo-white.png";
import stripeTrusted from "../../assets/images/wl-security-badge-stripe.png";
import { WLButton } from "../../../Common/components/WLButton/WLButton";
import { useBreakpoint } from "../../../Common/utils/common";
import { FormProvider } from "react-hook-form";
import { WebPaymentLanding } from "../WebPaymentLanding/WebPaymentLanding";
import { WLCOLORS } from "../../../Common/config/global";
import StripeExpressPaymentForm from "./StripeExpressPaymentForm/StripeExpressPaymentForm";
import { useWLConfigProvider } from "../../CommonStores/wlConfig/useWLConfigProvider";
import { useSuperGiveAwayRunningGiveawayProvider } from "../../CommonStores/giveaways/useGiveAwaysProvider";
import { PW_WEB_TEST_CONFIG } from "../../../Common/testConstants";

import { trackSignupInitiateCheckout } from "../../services/marketing";

const CompleteProfile = lazy(() => import("./CompleteProfile"));
const OrderSummaryPlainVersion = lazy(
  () => import("./OrderSummaryPlainVersion/OrderSummaryPlainVersion")
);
const OrderSummaryPlainVersionV2 = lazy(
  () => import("./OrderSummaryPlainVersionV2/OrderSummaryPlainVersion")
);

(ReactFbq as any).initialize({ id: "3689540701275716" });
(ReactFbq as any).pageView();
export const WebSignUp = () => {
  // Must run before store reads / promo/subscription providers so bootstrap settles mount prereqs first.
  useWebSignupBootstrapProvider();
  const { selectedSubscription } = useWebSignupStore();
  const { useFormMethods, error, redirectToApp, setRedirectToApp, onSubmit } =
    useWebSignupProvider();
  useWebSignupPromoCodeProvider();
  useSubscriptionsProvider();
  useGetReferralEntries();
  useWLConfigProvider();
  useSuperGiveAwayRunningGiveawayProvider();

  const initiateCheckoutFiredRef = useRef(false);

  useIonViewDidEnter(() => {
    auth.signOut();
  });

  useEffect(() => {
    if (
      selectedSubscription &&
      !isNull(selectedSubscription) &&
      !initiateCheckoutFiredRef.current
    ) {
      initiateCheckoutFiredRef.current = true;
      trackSignupInitiateCheckout({ subscription: selectedSubscription });
    }
  }, [selectedSubscription]);

  const isMobile = useBreakpoint() === "mobile";

  return (
    <IonPage className={s.page}>
      <IonContent className={s.content} fullscreen>
        {isMobile && (
          <div className={s.logoMobileContainerPlain}>
            <img className={s.logo} src={brandLogo} alt="" loading="eager" decoding="async" />
          </div>
        )}

        <div className={s.body}>
          <div className={s.innerContainer}>
            <img className={s.logo} src={brandLogo} alt="" loading="eager" decoding="async" />

            <FormProvider {...useFormMethods}>
              <form
                className={s.mainDetails}
                onSubmit={onSubmit}
              >
                <div className={cx(s.leftDetails, "ion-no-padding")}>
                  <div id={PW_WEB_TEST_CONFIG.orderSummaryPlain.containerV1}>
                    <Suspense>
                      <OrderSummaryPlainVersion />
                    </Suspense>
                  </div>
                  <div id={PW_WEB_TEST_CONFIG.orderSummaryPlain.containerV2}>
                    <Suspense>
                      <OrderSummaryPlainVersionV2 />
                    </Suspense>
                  </div>

                  <StripeExpressPaymentForm
                    setRedirectToApp={setRedirectToApp}
                  />
                  <div className={s.header}>
                    <IonLabel
                      className={cx(
                        s.headerTitle,
                        "win-header-5 medium winnersans primary-500"
                      )}
                      id="enter-details-title"
                    >
                      ENTER DETAILS
                    </IonLabel>
                    <IonLabel
                      className={cx(
                        s.headerDesc,
                        "win-body medium gilroy light-dark"
                      )}
                    >
                      Fill in your details so we know how to
                      {isMobile && <br />} contact you.
                    </IonLabel>
                  </div>

                  <Suspense>
                    <CompleteProfile useFormMethods={useFormMethods} />
                  </Suspense>

                  {!isMobile ? (
                    <>
                      <StripeForm />

                      <WLButton
                        type="submit"
                        disabled={
                          selectedSubscription === undefined ||
                          isNull(selectedSubscription)
                        }
                        className={cx(
                          s.submitBtn,
                          "win-header-5 black winnersans white red-submit-button"
                        )}
                        id="wl-sign-up-v3"
                        data-testid={PW_WEB_TEST_CONFIG.actionBtns.submitBtn}
                        isFormComponent
                      >
                        JOIN NOW
                      </WLButton>

                      <div className={s.footer}>
                        <div id="stripe-trusted-banner">
                          <IonLabel
                            className={cx(
                              s.cancelNote,
                              "win-sub medium gilroy ion-text-center"
                            )}
                          >
                            No lock-ins, no dramas - cancel anytime!
                          </IonLabel>

                          <img
                            className={s.stripeTrusted}
                            src={stripeTrusted}
                            alt=""
                            loading="lazy"
                            decoding="async"
                          />
                        </div>

                        <IonLabel
                          className={cx(
                            s.servicePolicy,
                            "win-small regular gilroy light-dark ion-text-center"
                          )}
                        >
                          By joining, you agree to Winners Locker's{" "}
                          <a
                            className={s.link}
                            onClick={() => {
                              window.open(
                                "https://winnerslocker.com.au/website-terms-and-conditions"
                              );
                            }}
                          >
                            Website Terms & Conditions
                          </a>{" "}
                          and{" "}
                          <a
                            className={s.link}
                            onClick={() => {
                              window.open(
                                "https://winnerslocker.com.au/terms-and-conditions"
                              );
                            }}
                          >
                            Membership Terms & Conditions
                          </a>
                          . We handle your personal information as set out in
                          our{" "}
                          <a
                            className={s.link}
                            onClick={() => {
                              window.open(
                                "https://winnerslocker.com.au/privacy-policy"
                              );
                            }}
                          >
                            Privacy Policy
                          </a>
                          .
                        </IonLabel>
                      </div>
                    </>
                  ) : null}
                </div>

                <div className={cx(s.rightDetails, "ion-no-padding")}>
                  {isMobile ? (
                    <>
                      <StripeForm />

                      <WLButton
                        type="submit"
                        disabled={
                          selectedSubscription === undefined ||
                          isNull(selectedSubscription)
                        }
                        className={cx(
                          s.submitBtn,
                          "win-header-5 black winnersans white red-submit-button"
                        )}
                        id="wl-sign-up-v3"
                        data-testid={PW_WEB_TEST_CONFIG.actionBtns.submitBtn}
                        isFormComponent
                      >
                        JOIN NOW
                      </WLButton>

                      <div className={s.footer}>
                        <div id="stripe-trusted-banner">
                          <IonLabel
                            className={cx(
                              s.cancelNote,
                              "win-sub medium gilroy ion-text-center"
                            )}
                          >
                            No lock-ins, no dramas - cancel anytime!
                          </IonLabel>

                          <img
                            className={s.stripeTrusted}
                            src={stripeTrusted}
                            alt=""
                            loading="lazy"
                            decoding="async"
                          />
                        </div>

                        <IonLabel
                          className={cx(
                            s.servicePolicy,
                            "win-small regular gilroy light-dark ion-text-center"
                          )}
                        >
                          By joining, you agree to Winners Locker's{" "}
                          <a
                            className={s.link}
                            onClick={() => {
                              window.open(
                                "https://winnerslocker.com.au/website-terms-and-conditions"
                              );
                            }}
                          >
                            Website Terms & Conditions
                          </a>{" "}
                          and{" "}
                          <a
                            className={s.link}
                            onClick={() => {
                              window.open(
                                "https://winnerslocker.com.au/terms-and-conditions"
                              );
                            }}
                          >
                            Membership Terms & Conditions
                          </a>
                          . We handle your personal information as set out in
                          our{" "}
                          <a
                            className={s.link}
                            onClick={() => {
                              window.open(
                                "https://winnerslocker.com.au/privacy-policy"
                              );
                            }}
                          >
                            Privacy Policy
                          </a>
                          .
                        </IonLabel>
                      </div>
                    </>
                  ) : null}
                </div>
              </form>
            </FormProvider>
          </div>

          <IonModal isOpen={redirectToApp} className={s.redirectToAppModal}>
            <WebPaymentLanding
              redirectToApp
              onCloseAsModal={() => {
                setRedirectToApp(false);
              }}
            />
          </IonModal>

          <IonToast
            message={error}
            isOpen={!isEmpty(error)}
            duration={2000}
            color={WLCOLORS.danger}
          />
        </div>
      </IonContent>
    </IonPage>
  );
};
